import {useEffect, useMemo, useRef, useState} from 'react'
import {
  IconArmchair, IconArrowDown, IconArrowLeft, IconArrowUp, IconBabyCarriage,
  IconBinaryTree2, IconBriefcase,
  IconCalendarEvent, IconClipboard, IconClipboardList, IconCoffin, IconDeviceFloppy, IconDroplet,
  IconFlame, IconGrave, IconHeartBroken, IconHome, IconMedal, IconPencil, IconPhotoUp,
  IconPlaneArrival, IconPlaneDeparture, IconPlayerPlay, IconPlus, IconRings, IconSchool,
  IconCrop, IconPhoto, IconStar, IconTrash, IconX,
} from '@tabler/icons-react'
import {apiGet} from './api/client'
import {useApiToken} from './api/useApiToken'
import {formatDate, formFromPerson, parseDate, savePerson, type PersonForm} from './api/edit'
import {emptyJob, isOccupation, jobBadDates, jobFromEvent, jobIsEmpty, jobLabel, type JobEntry} from './api/jobs'
import {
  COMMON_COUNT, emptyEvent, eventBadDates, eventFromRaw, eventIsEmpty, EVENT_TYPES,
  EV_BIRTH, EV_BURIAL, EV_DEATH, typeRu, type EventEntry,
} from './api/events'
import {
  emptyName, nameIsEmpty, nameLabel, NAME_TYPES, nameTypeRu, ORIGIN_TYPES, type NameEntry,
} from './api/names'
import {isImage, isMedia, looksVideo, MAX_PHOTO, sizeLimit, uploadPhoto} from './api/media'
import {kinship} from './api/kin'
import {createNote, loadNotes, noteLabel, noteText, updateNote, type NoteRaw} from './api/notes'
import {
  addChild, addToFamily, deletePerson, emptyUnion, saveUnions, setParents, unionsOf, type Union,
} from './api/relations'
import {
  famsOf, fullName, isDeceased, kidsOf, lifespan, parentsOf, photoOf, placePath, shortName,
  sibsOf,
} from './api/tree'
import type {TreeIndex} from './api/types'
import CropPicker from './CropPicker'
import MediaPicker from './MediaPicker'
import MediaViewer from './MediaViewer'
import NewPersonDialog from './NewPersonDialog'
import Overlay from './Overlay'
import PersonPicker from './PersonPicker'
import PlacePicker from './PlacePicker'
import Portrait from './Portrait'

interface Props {
  ix: TreeIndex
  handle: string
  onSaved: () => void
  onOpenPerson: (h: string) => void
  onShowInTree: (h: string) => void
  onBack: () => void
}

const GENDERS: [number, string][] = [[1, 'Мужской'], [0, 'Женский'], [2, 'Неизвестен']]

/** Свои подписи там, где общий список из api/events звучит не по-нашему:
 *  Occupation у нас «Работа», а брак и развод — события семьи, их в
 *  списке личных типов нет вовсе. Остальное переводит typeRu. */
const EVENT_RU: Record<string, string> = {
  Birth: 'Рождение', Death: 'Смерть', Burial: 'Погребение', Marriage: 'Брак',
  Divorce: 'Развод', Residence: 'Проживание', Occupation: 'Работа',
  Education: 'Образование', Baptism: 'Крещение', Christening: 'Крещение',
  Retirement: 'Выход на пенсию', Military_Service: 'Военная служба',
  Emigration: 'Эмиграция', Immigration: 'Иммиграция', Census: 'Перепись',
}

/** Пиктограммы событий — монотонные, как в Gramps: в ленте важен тип
 *  события, а не цвет, и пёстрый ряд читался бы хуже однотонного. */
const EVENT_ICON: Record<string, typeof IconCalendarEvent> = {
  Birth: IconBabyCarriage, Death: IconGrave, Burial: IconCoffin, Cremation: IconFlame,
  Marriage: IconRings, Divorce: IconHeartBroken, Residence: IconHome, Occupation: IconBriefcase,
  Education: IconSchool, Baptism: IconDroplet, Christening: IconDroplet,
  Retirement: IconArmchair, Military_Service: IconMedal,
  Emigration: IconPlaneDeparture, Immigration: IconPlaneArrival, Census: IconClipboardList,
}

/** Откуда событие: своё, семейное или чужое, но близкой родни. */
type EvCat = 'own' | 'family' | 'kin'

const CATS: [EvCat, string][] = [
  ['own', 'Личные'],
  ['family', 'Семья'],
  ['kin', 'Ближайшая родня'],
]

/** У родни берём только рождение и смерть: их проживания и места работы
 *  залили бы ленту так, что своей жизни в ней стало бы не видно. */
const KIN_EVENTS = new Set(['Birth', 'Death'])

/** Достоверность цитаты — числом в базе, словами в интерфейсе. */
const CONFIDENCE = ['очень низкая', 'низкая', 'обычная', 'высокая', 'очень высокая']

/** `change` в Gramps — unix-секунды. Показываем как ДД.ММ.ГГГГ. */
function stamp(sec: number): string {
  if (!sec) return '—'
  const d = new Date(sec * 1000)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()}`
}

/** Превью ещё не загруженного снимка: objectURL живёт ровно столько,
 *  сколько показывается плитка. */
function NewShot({blob}: {blob: Blob}) {
  const url = useMemo(() => URL.createObjectURL(blob), [blob])
  useEffect(() => () => URL.revokeObjectURL(url), [url])
  return <span className="pending"><img src={url} alt="" /></span>
}

interface NoteEntry {
  /** null у ещё не созданной заметки */
  handle: string | null
  text: string
  /** подпись типа: «из импорта», «о снимке» — пусто у обычной заметки */
  label: string
}

interface Kin {
  father: string | null
  mother: string | null
  /* Семей у человека может быть несколько: второй брак — это вторая
     семья со своими детьми и своей датой свадьбы. Дети принадлежат
     семье, а не человеку, поэтому и лежат внутри. */
  unions: Union[]
}

export default function PersonPage({ix, handle, onSaved, onOpenPerson, onShowInTree, onBack}: Props) {
  const [form, setForm] = useState<PersonForm | null>(null)
  const [jobs, setJobs] = useState<JobEntry[]>([])
  const [jobsWas, setJobsWas] = useState<JobEntry[]>([])
  const [kin, setKin] = useState<Kin | null>(null)
  const [was, setWas] = useState<Kin | null>(null)
  const [photoBlob, setPhotoBlob] = useState<Blob | null>(null)
  const [photoOut, setPhotoOut] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)
  const [saved, setSaved] = useState(false)
  // карточка открывается на просмотр: смотрят её куда чаще, чем правят,
  // и случайная правка в открытой форме опаснее лишнего клика
  const [editing, setEditing] = useState(false)
  const [reloadForm, setReloadForm] = useState(0)
  const [notes, setNotes] = useState<NoteEntry[]>([])
  const [notesWas, setNotesWas] = useState<NoteEntry[]>([])
  const [cites, setCites] = useState<{page: string; conf: number; source: string}[]>([])
  const galRef = useRef<HTMLInputElement>(null)
  const [addMedia, setAddMedia] = useState<Blob[]>([])
  // снимки, отвязываемые от человека; применяются при сохранении
  const [dropMedia, setDropMedia] = useState<string[]>([])
  const [meta, setMeta] = useState<{id: string; changed: number; priv: boolean} | null>(null)
  // подтверждение удаления: человек уносит с собой связи, и об этом
  // надо сказать до, а не после
  const [killing, setKilling] = useState(false)
  /* Дата свадьбы — событие СЕМЬИ, а не человека: в его списке событий
     её нет вовсе, поэтому и читаем, и пишем отдельно от формы. */

  /* Прочие события человека — проживание, служба, учёба. Рождение и смерть
     сюда не берём: у персоны на них указатели по позиции в списке, и
     свободное удаление ломает привязку. Они правятся в «Сведениях». */
  const [own, setOwn] = useState<EventEntry[]>([])
  const [ownWas, setOwnWas] = useState<EventEntry[]>([])
  /* Строки, где развернули полный список типов. Разворачиваем и сами,
     если у события тип из редких — иначе его не видно в своём же поле. */
  const [wide, setWide] = useState<Set<number>>(new Set())
  /* Редкие поля имени (приставка, прозвище, титул) открываются по кнопке:
     заполнены они у единиц, а в форме занимали три ряда у каждого имени. */
  const [moreName, setMoreName] = useState<Set<number>>(new Set())
  const jwt = useApiToken()
  /* Главный снимок и область лица на нём. Главный — просто первый в
     media_list, поэтому «сделать главным» это перестановка; область
     живёт в ССЫЛКЕ на снимок, а не в самом снимке: на групповой
     фотографии у каждого своё лицо. */
  const [mainMedia, setMainMedia] = useState<string | null>(null)
  const [mainRect, setMainRect] = useState<number[] | null>(null)
  const [cropping, setCropping] = useState<string | null>(null)
  /* Снимки из архива, выбранные в этой правке: файл уже загружен, надо
     лишь привязать его к человеку. */
  const [addExisting, setAddExisting] = useState<string[]>([])
  const [picking, setPicking] = useState(false)
  /** какой снимок галереи открыт во весь экран */
  const [viewing, setViewing] = useState<number | null>(null)
  /* Добавление ребёнка прямо из просмотра. Раньше это жило только в
     режиме правки и только выбором из уже заведённых: завести нового
     из карточки было нельзя вовсе, а найти сам выбор — тем более. */
  const [addKid, setAddKid] = useState<{family: string; mode: 'pick' | 'new'} | null>(null)
  /* Новый ребёнок в режиме правки: номер семьи в форме. Человека создаём
     БЕЗ привязки, а в семью он попадает вместе с сохранением формы —
     иначе привязка ушла бы в базу, а форма о ней не узнала и стёрла бы
     её обратно при сохранении. */
  const [newKid, setNewKid] = useState<number | null>(null)

  async function kill() {
    setBusy(true)
    setErr('')
    try {
      await deletePerson(handle)
      onSaved()
      onBack()
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Не удалось удалить')
      setBusy(false)
      setKilling(false)
    }
  }

  const person = ix.people.get(handle)

  /* В правке всегда есть хотя бы одна семья.
   *
   * У бездетного и неженатого человека семей нет вовсе, и в форме не
   * появлялось ни «Партнёр», ни «Дети» — только кнопка «добавить ещё
   * одну семью», за которой не угадать, что дети живут именно там.
   * Пустая семья ничего не создаёт: при сохранении она отбрасывается,
   * если в ней так и не появилось ни супруга, ни ребёнка. */
  useEffect(() => {
    if (!editing) return
    setKin(s => (s && !s.unions.length ? {...s, unions: [emptyUnion()]} : s))
  }, [editing])

  useEffect(() => {
    // родство берём из уже загруженного индекса — оно там полное
    const fam = ix.famOfChild.get(handle)
    const ps = parentsOf(ix, handle)
    const start: Kin = {
      father: fam?.father_handle ?? (ps[0] || null),
      mother: fam?.mother_handle ?? (ps[1] || null),
      unions: unionsOf(ix, handle),
    }
    setKin(start)
    setWas(start)
    setPhotoBlob(null)
    setPhotoOut(false)
    setSaved(false)

    let alive = true
    ;(async () => {
      try {
        const p = await apiGet<Record<string, unknown>>(`/people/${handle}`)
        const refs = (p.event_ref_list as {ref: string}[] | undefined) || []
        // тянем ВСЕ события человека одним заходом: из них берутся и даты,
        // и погребение (оно ищется по типу), и места работы
        const evs = await Promise.all(refs.map(r => apiGet<Record<string, unknown>>(`/events/${r.ref}`)))
        if (!alive) return
        setForm(formFromPerson(p as never))
        const found = evs.filter(ev => isOccupation(ev.type)).map(ev => jobFromEvent(ev as never))
        setJobs(found)
        setJobsWas(found)
        // всё, кроме работы: рождение, смерть, проживание, служба —
        // в Gramps это один список, и правятся они одинаково
        const rest = evs
          .filter(ev => !isOccupation(ev.type))
          .map(ev => eventFromRaw(ev as never))
        setOwn(rest)
        setOwnWas(rest)
        setAddMedia([])
        setDropMedia([])
        const first = (p.media_list as {ref: string; rect?: number[] | null}[] | undefined)?.[0]
        setAddExisting([])
        setPicking(false)
        setMainMedia(first?.ref || null)
        setMainRect(first?.rect && first.rect.length === 4 ? first.rect : null)
        setCropping(null)
        setMeta({
          id: String(p.gramps_id || ''),
          changed: Number(p.change || 0),
          priv: !!p.private,
        })

        const noteHandles = (p.note_list as string[] | undefined) || []
        const loaded: NoteRaw[] = noteHandles.length ? await loadNotes(noteHandles) : []
        if (!alive) return
        const asText = loaded.map(n => ({handle: n.handle, text: noteText(n), label: noteLabel(n)}))
        setNotes(asText)
        setNotesWas(asText)

        // цитаты тянем вместе с источником: без названия источника
        // «страница 12» ничего не значит
        const citeHandles = (p.citation_list as string[] | undefined) || []
        const cs = await Promise.all(citeHandles.map(h => apiGet<Record<string, unknown>>(`/citations/${h}`)))
        const srcs = await Promise.all(
          cs.map(cc => cc.source_handle
            ? apiGet<Record<string, unknown>>(`/sources/${cc.source_handle}`).catch(() => null)
            : Promise.resolve(null)),
        )
        if (!alive) return
        setCites(cs.map((cc, i) => {
          const src = srcs[i] as {title?: string; author?: string} | null
          return {
            page: String(cc.page || ''),
            conf: Number(cc.confidence ?? 2),
            source: [src?.title, src?.author].filter(Boolean).join(' · '),
          }
        }))
      } catch (e) {
        if (alive) setErr(e instanceof Error ? e.message : 'Не удалось загрузить человека')
      }
    })()
    return () => { alive = false }
  }, [ix, handle, reloadForm])

  const preview = useMemo(() => (photoBlob ? URL.createObjectURL(photoBlob) : null), [photoBlob])
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview) }, [preview])

  const existingPhoto = photoOf(person)
  /* Показываем то, что получится: выбранный главный снимок и обрезку по
     выбранной области — ещё до сохранения. */
  const shownPhoto = mainMedia || existingPhoto
  const photoUrl = preview
    ? preview
    : photoOut || !shownPhoto
      ? null
      : mainRect && mainRect.length === 4
        ? `/api/media/${shownPhoto}/cropped/${mainRect[0]}/${mainRect[1]}/${mainRect[2]}/${mainRect[3]}/thumbnail/480?jwt=${jwt}`
        : `/api/media/${shownPhoto}/thumbnail/480?jwt=${jwt}`

  function takeImage(blob: Blob | undefined | null) {
    if (!blob) return
    if (!isImage(blob)) { setErr('Это не изображение'); return }
    if (blob.size > MAX_PHOTO) { setErr('Файл больше 20 МБ — уменьшите его'); return }
    setErr('')
    setPhotoBlob(blob)
    setPhotoOut(false)
  }

  // вставка из буфера прямо на странице
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const item = [...(e.clipboardData?.items || [])].find(i => i.type.startsWith('image/'))
      if (!item) return
      e.preventDefault()
      takeImage(item.getAsFile())
    }
    window.addEventListener('paste', onPaste)
    return () => window.removeEventListener('paste', onPaste)
  }, [])

  const badWed = (kin?.unions || []).some(u => !!u.wedDate && !parseDate(u.wedDate))
  const badJobs = jobs.some(j => !jobIsEmpty(j) && jobBadDates(j))
  const badOwn = own.some(e => !eventIsEmpty(e) && eventBadDates(e))
  const canSave = !!form && !busy && !badWed && !badJobs && !badOwn

  async function save(e?: React.FormEvent) {
    e?.preventDefault()
    if (!form || !kin || !was || !canSave) return
    setBusy(true)
    setErr('')
    try {
      // заметки создаём заранее: их handle нужен персоне при записи
      const addNotes: string[] = []
      for (const n of notes) {
        const text = n.text.trim()
        if (n.handle) {
          const before = notesWas.find(x => x.handle === n.handle)
          if (before && before.text !== n.text) await updateNote(n.handle, n.text)
        } else if (text) {
          addNotes.push(await createNote(n.text))
        }
      }
      const alive = new Set(notes.map(n => n.handle).filter(Boolean) as string[])
      const delNotes = notesWas.map(n => n.handle).filter((h): h is string => !!h && !alive.has(h))

      const addedMedia: string[] = []
      for (const b of addMedia) addedMedia.push(await uploadPhoto(b))

      await savePerson(handle, form, jobs, jobsWas, {blob: photoBlob, remove: photoOut},
                       {addNotes, delNotes, dropMedia,
                        events: own, eventsWas: ownWas,
                        mainMedia: mainMedia || undefined, mainRect,
                        addMedia: [...addedMedia, ...addExisting]})

      if (kin.father !== was.father || kin.mother !== was.mother) {
        await setParents(ix, handle, kin.father, kin.mother)
      }
      await saveUnions(ix, handle, kin.unions, was.unions)

      setSaved(true)
      setEditing(false)
      onSaved()
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : 'Не удалось сохранить')
    } finally {
      setBusy(false)
    }
  }

  const set = <K extends keyof PersonForm>(k: K, v: PersonForm[K]) =>
    setForm(f => (f ? {...f, [k]: v} : f))
  const setJob = (i: number, patch: Partial<JobEntry>) =>
    setJobs(list => list.map((j, k) => (k === i ? {...j, ...patch} : j)))
  const setOwnEv = (i: number, patch: Partial<EventEntry>) =>
    setOwn(list => list.map((e, k) => (k === i ? {...e, ...patch} : e)))
  /* Рождение, смерть и погребение живут в общем списке событий, но в
     «Сведениях» их показываем отдельной строкой: это первое, что ищут
     в карточке, и гонять человека в ленту событий за датой рождения
     было бы издевательством. */
  const setName = (i: number, patch: Partial<NameEntry>) =>
    setForm(f => f && {...f, names: f.names.map((n, k) => (k === i ? {...n, ...patch} : n))})

  /* Основное имя — просто первое в списке: так его хранит и Gramps.
     Меняем местами, а не копируем, чтобы прежнее не потерялось. */
  const makeMainName = (i: number) =>
    setForm(f => {
      if (!f) return f
      const names = [...f.names]
      ;[names[0], names[i]] = [names[i], names[0]]
      return {...f, names}
    })

  const ownAt = (t: number) => own.find(e => e.type === t)

  /* Галочка «нет в живых» — короткий путь к событию смерти: ставит
     событие без даты, снимает — убирает его. Заполненное событие она
     тоже уберёт, поэтому о потере даты предупреждаем заранее. */
  const deathEv = ownAt(EV_DEATH)
  const deathHasData = !!deathEv && !!(deathEv.from || deathEv.to || deathEv.place || deathEv.desc)
  const setDeceased = (on: boolean) => {
    if (on === !!deathEv) return
    if (on) {
      setOwn(list => [...list, {...emptyEvent(), type: EV_DEATH}])
      return
    }
    // через dropOwn, а не фильтром: он ещё и сдвигает отметки
    // «развёрнутый список типов», привязанные к номеру строки
    const i = own.findIndex(e => e.type === EV_DEATH)
    if (i >= 0) dropOwn(i)
  }
  const factOf = (t: number) => {
    const e = ownAt(t)
    if (!e) return ''
    const years = e.from && e.to ? `${e.from} — ${e.to}` : e.from || e.to
    return [years, placePath(ix, e.place)].filter(Boolean).join(', ')
  }
  const burialCoords = (() => {
    const p = ownAt(EV_BURIAL)?.place
    return p ? coordsOf(ix, p) : null
  })()

  const wideAt = (i: number, type: number) =>
    wide.has(i) || !EVENT_TYPES.slice(0, COMMON_COUNT).some(([v]) => v === type)
  const evTypes = (i: number, type: number) =>
    wideAt(i, type) ? EVENT_TYPES : EVENT_TYPES.slice(0, COMMON_COUNT)
  /* Порядок событий — это и есть event_ref_list в базе: в Gramps события
     двигают стрелками, и хронология рассказа задаётся руками, а не датами
     (у половины событий дат нет вовсе). */
  const moveOwn = (i: number, d: number) => {
    const j = i + d
    if (j < 0 || j >= own.length) return
    setOwn(list => {
      const next = [...list]
      ;[next[i], next[j]] = [next[j], next[i]]
      return next
    })
    setWide(w => {
      const next = new Set<number>()
      w.forEach(k => next.add(k === i ? j : k === j ? i : k))
      return next
    })
  }

  /* Отметки «развёрнут» привязаны к номеру строки, поэтому при удалении
     их надо сдвинуть — иначе длинный список всплывёт у соседа. */
  const dropOwn = (i: number) => {
    setOwn(list => list.filter((_, k) => k !== i))
    setWide(w => new Set([...w].filter(k => k !== i).map(k => (k > i ? k - 1 : k))))
  }
  const setK = <K extends keyof Kin>(k: K, v: Kin[K]) =>
    setKin(s => (s ? {...s, [k]: v} : s))
  const setUnion = (i: number, patch: Partial<Union>) =>
    setKin(s => s && {...s, unions: s.unions.map((u, k) => (k === i ? {...u, ...patch} : u))})
  const dropUnion = (i: number) =>
    setKin(s => s && {...s, unions: s.unions.filter((_, k) => k !== i)})
  /** дети со всех семей — для предупреждения при удалении человека */
  const allKids = (kin?.unions || []).flatMap(u => u.children)

  /** Привязать уже заведённого ребёнка к конкретной семье. */
  async function addExistingKid(family: string, child: string) {
    setBusy(true)
    setErr('')
    try {
      // без семьи addChild заведёт её сам — с одним родителем
      if (family) await addToFamily(family, child)
      else await addChild(ix, handle, child)
      setAddKid(null)
      onSaved()
      setReloadForm(n => n + 1)
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Не удалось добавить ребёнка')
    } finally {
      setBusy(false)
    }
  }

  const busyAll: (string | null)[] = [
    handle, kin?.father ?? null, kin?.mother ?? null,
    ...(kin?.unions || []).flatMap(u => [u.spouse, ...u.children]),
  ]

  /**
   * Лента событий: свои, семейные и близкой родни.
   *
   * Брак и развод привязаны к СЕМЬЕ, а не к человеку — в его
   * event_ref_list их нет вовсе, и без этого дата свадьбы не
   * показывалась нигде, хотя в базе лежала.
   *
   * Рождения и смерти родителей, супругов, детей и братьев берём затем,
   * что жизнь человека складывается и из них: без этой строки в ленте
   * не видно, что сын родился через год после свадьбы.
   */
  const events = useMemo(() => {
    const p = ix.people.get(handle)
    type Src = {ref: string; with: string; cat: EvCat; only?: Set<string>}
    const src: Src[] = (p?.event_ref_list || []).map(r => ({ref: r.ref, with: '', cat: 'own'}))

    for (const f of famsOf(ix, handle)) {
      const other = f.father_handle === handle ? f.mother_handle : f.father_handle
      const who = other ? shortName(ix.people.get(other)) : ''
      for (const r of f.event_ref_list || []) src.push({ref: r.ref, with: who, cat: 'family'})
    }

    const near = new Set<string>([
      ...parentsOf(ix, handle), ...kidsOf(ix, handle), ...sibsOf(ix, handle),
      ...famsOf(ix, handle).map(f => (f.father_handle === handle ? f.mother_handle : f.father_handle))
        .filter((x): x is string => !!x),
    ])
    for (const h of near) {
      const term = kinship(ix, handle, h).toLowerCase()
      const who = [shortName(ix.people.get(h)), term].filter(Boolean).join(', ')
      for (const r of ix.people.get(h)?.event_ref_list || []) {
        src.push({ref: r.ref, with: who, cat: 'kin', only: KIN_EVENTS})
      }
    }

    return src
      .map(x => ({...x, e: ix.events.get(x.ref)}))
      .filter(x => !!x.e)
      .filter(x => !x.only || x.only.has(typeof x.e!.type === 'string' ? x.e!.type : ''))
      .map(({e, with: who, cat}) => {
        const dv = e!.date?.dateval
        return {
          cat,
          type: typeof e!.type === 'string' ? e!.type : '',
          date: formatDate(dv),
          // для сортировки: год-месяц-день одним числом, без даты — в конец
          key: dv?.[2] ? dv[2] * 10000 + (dv[1] || 0) * 100 + (dv[0] || 0) : Infinity,
          place: e!.place ? placePath(ix, e!.place) : '',
          desc: [e!.description || '', who].filter(Boolean).join(' · '),
        }
      })
      .sort((a, b) => a.key - b.key)
  }, [ix, handle])

  const [cats, setCats] = useState<Set<EvCat>>(new Set<EvCat>(['own', 'family', 'kin']))
  const shownEvents = events.filter(e => cats.has(e.cat))

  /* Главное фото первым, остальное по дате съёмки. Главное — это
     аватарка, её ищут в галерее первой; у снимков без даты порядок
     остаётся тот, в котором их привязали. */
  const gallery = [
    ...(person?.media_list?.map(m => m.ref) || []),
    ...addExisting,
  ]
    .filter(m => !dropMedia.includes(m))
    .sort((a, b) => {
      if (a === mainMedia) return -1
      if (b === mainMedia) return 1
      const sa = ix.media.get(a)?.sort || 0
      const sb = ix.media.get(b)?.sort || 0
      if (!sa || !sb) return sa === sb ? 0 : sa ? -1 : 1
      return sa - sb
    })
  const siblings = sibsOf(ix, handle)

  if (!person) {
    return (
      <div className="page">
        <div className="err">Человек не найден — возможно, его удалили.</div>
      </div>
    )
  }

  return (
    <form className="page person" onSubmit={save}>
      <div className="pbar">
        <button type="button" className="btn ghost" onClick={onBack}>
          <IconArrowLeft size={16} stroke={1.8} /> Назад
        </button>
        <div className="spacer" />
        <button type="button" className="btn" onClick={() => onShowInTree(handle)}>
          <IconBinaryTree2 size={15} stroke={1.7} /> Показать в древе
        </button>
        {editing ? (
          <>
            <button type="button" className="btn"
                    onClick={() => { setEditing(false); setReloadForm(n => n + 1); setErr('') }}>
              <IconX size={15} stroke={1.8} /> Отмена
            </button>
            <button className="btn primary" disabled={!canSave}>
              <IconDeviceFloppy size={15} stroke={1.7} />
              {busy ? 'Сохраняем…' : 'Сохранить'}
            </button>
          </>
        ) : (
          <button type="button" className="btn primary" onClick={() => { setEditing(true); setSaved(false) }}>
            <IconPencil size={15} stroke={1.7} /> Изменить
          </button>
        )}
      </div>

      {err && <div className="err inline">{err}</div>}
      {saved && !editing && <div className="okline">Изменения сохранены</div>}

      <div className="phero">
        <div className="pportrait">
          {/* Лента и здесь, а не только в списках: человек чаще всего
              открывает именно карточку, и без неё было непонятно, жив ли
              человек, пока не долистаешь до Сведений. В режиме правки
              смотрим на список событий — лента появляется сразу, как
              добавили «Смерть», ещё до сохранения. */}
          <div className={'pshot big' + (photoUrl ? '' : ' empty')
                          + ((editing ? !!ownAt(EV_DEATH) : isDeceased(person)) ? ' dead' : '')}>
            {photoUrl ? <img src={photoUrl} alt="" /> : <span>нет фото</span>}
          </div>
          {editing && <div className="pacts">
            <input ref={fileRef} type="file" accept="image/*" hidden
                   onChange={e => takeImage(e.target.files?.[0])} />
            <button type="button" className="btn" onClick={() => fileRef.current?.click()}>
              <IconPhotoUp size={15} stroke={1.7} /> {photoUrl ? 'Заменить' : 'Выбрать файл'}
            </button>
            {/* Нужный кадр чаще всего уже в архиве — просто отмечен на
                другом человеке. Загружать его заново значило бы завести
                второй объект на ту же фотографию. */}
            <button type="button" className="btn" onClick={() => setPicking(true)}>
              <IconPhoto size={15} stroke={1.7} /> Из архива
            </button>
            {(existingPhoto || photoBlob) && !photoOut && (
              <button type="button" className="btn"
                      onClick={() => { setPhotoBlob(null); setPhotoOut(true) }}>
                <IconTrash size={15} stroke={1.7} /> Убрать
              </button>
            )}
            {photoOut && (
              <button type="button" className="btn" onClick={() => setPhotoOut(false)}>Вернуть прежнее</button>
            )}
            <span className="hint">
              <IconClipboard size={12} stroke={1.8} /> Ctrl+V вставит картинку из буфера
            </span>
          </div>}
        </div>

        <div className="pmain">
          <h1>{fullName(person)}</h1>
          <div className="psub">{lifespan(person) || 'даты неизвестны'}</div>
        </div>
      </div>

      {form && kin ? (
        <div className="pblocks">
          <div className="block">
            <div className="blabel">Сведения</div>
{editing ? (<>

            <label>
              Пол
              <div className="radios">
                {GENDERS.map(([v, label]) => (
                  <button type="button" key={v} className={form.gender === v ? 'on' : ''}
                          onClick={() => set('gender', v)}>
                    {label}
                  </button>
                ))}
              </div>
            </label>

            {/* Отдельного признака «умер» в Gramps нет — есть событие
                смерти. Но чаще всего известен только факт, и заводить
                ради него событие руками, выбирая тип из списка, — работа
                на ровном месте. Галочка делает это за один щелчок. */}
            <label className="check">
              <input type="checkbox" checked={!!ownAt(EV_DEATH)}
                     onChange={e => setDeceased(e.target.checked)} />
              <span>Человека нет в живых</span>
            </label>

            <span className="hint">
              Отметка заводит событие «Смерть» без даты — этого хватает,
              чтобы на портрете появилась траурная лента. Даты и места
              рождения, смерти и погребения заполняются ниже, в «Событиях»:
              у человека в Gramps нет поля «дата смерти», есть список
              событий.
              {deathHasData && ' Внимание: снятая отметка сотрёт записанные дату и место смерти.'}
            </span>
</>) : (<>
            <dl className="pview">
              <div className="fact">
                <dt>Пол</dt><dd>{GENDERS.find(g => g[0] === form.gender)?.[1] || '—'}</dd>
              </div>
              <div className="fact">
                <dt>Рождение</dt><dd>{factOf(EV_BIRTH) || '—'}</dd>
              </div>
              {/* Смерть и погребение показываем, только если есть что показать.
                  У живых эти строки — просто два прочерка, а живых в древе
                  больше половины. */}
              {ownAt(EV_DEATH) && (
                <div className="fact">
                  <dt>Смерть</dt><dd>{factOf(EV_DEATH) || 'дата неизвестна'}</dd>
                </div>
              )}
              {ownAt(EV_BURIAL) && (
                <div className="fact">
                  <dt>Погребение</dt>
                  <dd>
                    {factOf(EV_BURIAL) || 'место неизвестно'}
                    {burialCoords && (
                      <a className="geolink"
                         href={`https://www.openstreetmap.org/?mlat=${burialCoords.lat}&mlon=${burialCoords.long}#map=18/${burialCoords.lat}/${burialCoords.long}`}
                         target="_blank" rel="noreferrer">на карте</a>
                    )}
                  </dd>
                </div>
              )}
            </dl>
</>)}
          </div>

          <div className="block">
            <div className="blabel">Имена</div>
{editing ? (<>
            <div className="evedit">
              {form.names.map((n, i) => (
                <div className="job" key={i}>
                  <div className="namehead">
                    <select className="sel" value={n.type}
                            onChange={e => setName(i, {type: Number(e.target.value)})}>
                      {NAME_TYPES.map(([v, label]) => (
                        <option key={v} value={v}>{label}</option>
                      ))}
                    </select>
                    {i === 0
                      ? <span className="tagmain">основное</span>
                      : (
                        <>
                          <button type="button" className="btn tiny"
                                  onClick={() => makeMainName(i)}>Сделать основным</button>
                          <button type="button" className="icobtn" title="Убрать имя"
                                  onClick={() => setForm(f => f && {
                                    ...f, names: f.names.filter((_, k) => k !== i),
                                  })}>
                            <IconTrash size={15} stroke={1.7} />
                          </button>
                        </>
                      )}
                  </div>
                  <div className="two">
                    <label>
                      Фамилия
                      <input value={n.surname} onChange={e => setName(i, {surname: e.target.value})} />
                    </label>
                    <label>
                      Имя и отчество
                      <input value={n.first} onChange={e => setName(i, {first: e.target.value})} />
                    </label>
                  </div>
                  {moreName.has(i) ? (
                    <>
                      <div className="two">
                        <label>
                          Приставка фамилии
                          <input value={n.prefix} placeholder="де, фон, ван"
                                 onChange={e => setName(i, {prefix: e.target.value})} />
                        </label>
                        <label>
                          Происхождение фамилии
                          <select className="sel" value={n.origin}
                                  onChange={e => setName(i, {origin: Number(e.target.value)})}>
                            {ORIGIN_TYPES.map(([v, label]) => (
                              <option key={v} value={v}>{label}</option>
                            ))}
                          </select>
                        </label>
                      </div>
                      <div className="two">
                        <label>
                          Как звали дома
                          <input value={n.call} onChange={e => setName(i, {call: e.target.value})} />
                        </label>
                        <label>
                          Прозвище
                          <input value={n.nick} onChange={e => setName(i, {nick: e.target.value})} />
                        </label>
                      </div>
                      <div className="two">
                        <label>
                          Титул
                          <input value={n.title} onChange={e => setName(i, {title: e.target.value})} />
                        </label>
                        <label>
                          Суффикс
                          <input value={n.suffix} placeholder="мл., ст."
                                 onChange={e => setName(i, {suffix: e.target.value})} />
                        </label>
                      </div>
                    </>
                  ) : (
                    <button type="button" className="btn tiny wide"
                            onClick={() => setMoreName(s => new Set(s).add(i))}>
                      Приставка, прозвище, титул…
                    </button>
                  )}
                </div>
              ))}
              <button type="button" className="btn addrow"
                      onClick={() => setForm(f => f && {...f, names: [...f.names, emptyName(3)]})}>
                <IconPlus size={14} stroke={2} /> Добавить имя
              </button>
              <span className="hint">
                Имён может быть несколько — при рождении, в браке, «известен как».
                Основное показывается в списках и в древе. Отчество импорт положил
                вместе с именем, отдельного поля в данных нет.
              </span>
            </div>
</>) : (<>
            <dl className="pview">
              {form.names.filter((n, i) => i === 0 || !nameIsEmpty(n)).map((n, i) => (
                <div className="fact" key={i}>
                  <dt>{nameTypeRu(n.type)}</dt>
                  <dd>{nameLabel(n) || '—'}</dd>
                </div>
              ))}
            </dl>
</>)}
          </div>

          <div className="block">
            <div className="blabel">Отношения</div>
{editing ? (<>
            {/* Те же полосы, что и в просмотре: родители, братья и сёстры,
                партнёр, дети. Одинаковая раскладка в обоих режимах — чтобы
                после нажатия «Править» ничего не прыгало. */}
            <div className="kingroups">
              <div className="kingroup">
                <div className="kghead"><span>Родители</span></div>
                <div className="two">
                  <label>
                    Отец
                    <PersonPicker ix={ix} value={kin.father} onChange={v => setK('father', v)}
                                  exclude={busyAll.filter(x => x !== kin.father)} />
                  </label>
                  <label>
                    Мать
                    <PersonPicker ix={ix} value={kin.mother} onChange={v => setK('mother', v)}
                                  exclude={busyAll.filter(x => x !== kin.mother)} />
                  </label>
                </div>
              </div>

              {siblings.length > 0 && (
                <div className="kingroup">
                  <div className="kghead">
                    <span>Братья и сёстры</span>
                    <em className="kgwed">меняются у родителей — своей связи у них нет</em>
                  </div>
                  <KinCards list={siblings} ix={ix} onGo={onOpenPerson} empty="" />
                </div>
              )}

              {kin.unions.map((u, i) => (
                <div className="kingroup" key={u.family || `new${i}`}>
                  <div className="kghead">
                    <span>{kin.unions.length > 1 ? `Партнёр ${i + 1}` : 'Партнёр'}</span>
                    <button type="button" className="lnk" onClick={() => dropUnion(i)}>
                      отвязать семью
                    </button>
                  </div>
                  <label>
                    Супруг(а)
                    <PersonPicker ix={ix} value={u.spouse}
                                  onChange={v => setUnion(i, {spouse: v})}
                                  exclude={busyAll.filter(x => x !== u.spouse)} />
                  </label>
                  {u.spouse && (
                    <>
                      <div className="two" style={{marginTop: 9}}>
                        <label>
                          Дата свадьбы
                          <input value={u.wedDate} placeholder="дд.мм.гггг"
                                 className={u.wedDate && !parseDate(u.wedDate) ? 'bad' : ''}
                                 onChange={e => setUnion(i, {wedDate: e.target.value})} />
                        </label>
                        <label>
                          Место свадьбы
                          <PlacePicker ix={ix} value={u.wedPlace}
                                       onChange={v => setUnion(i, {wedPlace: v})} />
                        </label>
                      </div>
                      {/* Развод — такое же событие семьи, как свадьба, и
                          семью он не распускает: супруги в ней остаются,
                          иначе вместе со связью пропало бы родительство
                          детей. Поэтому поля рядом, а не «вместо». */}
                      <div className="two" style={{marginTop: 9}}>
                        <label>
                          Дата развода
                          <input value={u.divDate} placeholder="если был"
                                 className={u.divDate && !parseDate(u.divDate) ? 'bad' : ''}
                                 onChange={e => setUnion(i, {divDate: e.target.value})} />
                        </label>
                        <label>
                          Место развода
                          <PlacePicker ix={ix} value={u.divPlace}
                                       onChange={v => setUnion(i, {divPlace: v})} />
                        </label>
                      </div>
                      <span className="hint">
                        Развод записывается событием, а семья остаётся: дети
                        остаются детьми обоих. Год без числа тоже годится.
                      </span>
                    </>
                  )}
                  <div className="kidsedit">
                    <div className="blabel sm">Дети</div>
                    {u.children.map(c => (
                      <PersonPicker key={c} ix={ix} value={c}
                        onChange={v => setUnion(i, {children: v
                          ? u.children.map(x => (x === c ? v : x))
                          : u.children.filter(x => x !== c)})} />
                    ))}
                    <PersonPicker ix={ix} value={null} exclude={busyAll}
                                  onChange={v => v && setUnion(i, {children: [...u.children, v]})}
                                  placeholder="кто уже есть в древе — начните вводить имя" />
                    {/* Поле выше берёт только заведённых. Нового человека
                        оттуда не создать, а именно это чаще и нужно. */}
                    <button type="button" className="btn tiny wide"
                            onClick={() => setNewKid(i)}>
                      <IconPlus size={14} stroke={2} /> Завести нового ребёнка
                    </button>
                  </div>
                </div>
              ))}

              <div className="kingroup">
                <button type="button" className="btn addrow"
                        onClick={() => setKin(s => s && {...s, unions: [...s.unions, emptyUnion()]})}>
                  <IconPlus size={14} stroke={2} /> Добавить ещё одну семью
                </button>
                <span className="hint">
                  Второй брак — это вторая семья со своими детьми и своей датой
                  свадьбы. «Отвязать» убирает человека из семьи, но саму семью
                  оставляет: у второго супруга и детей она остаётся своей.
                </span>
              </div>
            </div>
</>) : (<>
            {/* Разложено как в самом Gramps: родители, братья и сёстры,
                партнёр с детьми — отдельными полосами, а не общей сеткой.
                Так видно, ЧЬИ дети и от какого брака, а это в родословной
                и есть главный вопрос. */}
            <div className="kingroups">
              <div className="kingroup">
                <div className="kghead">
                  <span>Родители</span>
                  <button type="button" className="lnk"
                          onClick={() => setEditing(true)}>
                    {kin.father || kin.mother ? 'изменить' : 'добавить родителей'}
                  </button>
                </div>
                <KinCards ix={ix} onGo={onOpenPerson} empty="не указаны"
                          list={[kin.father, kin.mother].filter((h): h is string => !!h)} />
              </div>

              {siblings.length > 0 && (
                <div className="kingroup">
                  <div className="kghead"><span>Братья и сёстры</span></div>
                  <KinCards list={siblings} ix={ix} onGo={onOpenPerson} empty="" />
                </div>
              )}

              {kin.unions.map((u, i) => (
                <div className="kingroup" key={u.family || `new${i}`}>
                  <div className="kghead">
                    <span>{kin.unions.length > 1 ? `Партнёр ${i + 1}` : 'Партнёр'}</span>
                    {(u.wedDate || u.wedPlace) && (
                      <em className="kgwed">
                        свадьба: {[u.wedDate, placePath(ix, u.wedPlace)].filter(Boolean).join(', ')}
                      </em>
                    )}
                    {(u.divDate || u.divPlace) && (
                      <em className="kgwed div">
                        развод: {[u.divDate, placePath(ix, u.divPlace)].filter(Boolean).join(', ')}
                      </em>
                    )}
                    <button type="button" className="lnk" onClick={() => setEditing(true)}>
                      изменить
                    </button>
                  </div>
                  <KinCards list={u.spouse ? [u.spouse] : []} ix={ix} onGo={onOpenPerson}
                            empty="не указан" />
                  <div className="kidsview">
                    <div className="kghead">
                      <span>Дети</span>
                      {u.family && (
                        <button type="button" className="lnk"
                                onClick={() => setAddKid({family: u.family!, mode: 'pick'})}>
                          добавить ребёнка
                        </button>
                      )}
                    </div>
                    <KinCards list={u.children} ix={ix} onGo={onOpenPerson}
                              empty="пока никого" />
                  </div>
                </div>
              ))}

              {/* У человека без семьи полос выше нет вовсе — и добавить
                  ребёнка было решительно некуда. Семья заведётся сама:
                  ребёнок без второго родителя это тоже семья, просто с
                  одним. */}
              {!kin.unions.length && (
                <div className="kingroup">
                  <div className="kghead">
                    <span>Дети</span>
                    <button type="button" className="lnk"
                            onClick={() => setAddKid({family: '', mode: 'pick'})}>
                      добавить ребёнка
                    </button>
                  </div>
                  <div className="muted">не указаны</div>
                </div>
              )}

              <div className="kingroup">
                <div className="kghead">
                  <span>{kin.unions.length ? 'Ещё семья' : 'Партнёр'}</span>
                  <button type="button" className="lnk" onClick={() => setEditing(true)}>
                    {kin.unions.length ? 'добавить вторую семью' : 'добавить супруга'}
                  </button>
                </div>
              </div>
            </div>
</>)}
          </div>

          {(events.length > 0 || editing) && (
            <div className="block">
              <div className="blabel">События</div>

              {/* Фильтры как в Gramps: лента у человека с роднёй длинная,
                  и «только своё» бывает нужнее полной хроники. */}
              <div className="evfilter">
                {CATS.map(([c, label]) => {
                  const n = events.filter(e => e.cat === c).length
                  if (!n) return null
                  const on = cats.has(c)
                  return (
                    <button type="button" key={c} className={'chip' + (on ? ' on' : '')}
                            onClick={() => setCats(prev => {
                              const next = new Set(prev)
                              // последнюю галку не снимаем: пустая лента
                              // выглядит поломкой, а не выбором
                              if (on && next.size > 1) next.delete(c)
                              else next.add(c)
                              return next
                            })}>
                      {label} <em>{n}</em>
                    </button>
                  )
                })}
              </div>

              {editing && (
                <div className="evedit">
                  {own.map((e, i) => (
                    <div className="job" key={e.handle || `new${i}`}>
                      <div className="two">
                        <label>
                          Что
                          <select className="sel" value={e.type}
                                  onChange={ev => {
                                    const v = Number(ev.target.value)
                                    if (v === -1) setWide(w => new Set(w).add(i))
                                    else setOwnEv(i, {type: v})
                                  }}>
                            {evTypes(i, e.type).map(([v, label]) => (
                              <option key={v} value={v}>{label}</option>
                            ))}
                            {!wideAt(i, e.type) && <option value={-1}>Другой тип…</option>}
                          </select>
                        </label>
                        <label>
                          Место
                          <PlacePicker ix={ix} value={e.place}
                                       onChange={v => setOwnEv(i, {place: v})} />
                        </label>
                      </div>
                      <div className="jobdates">
                        <input placeholder="дата: дд.мм.гггг" value={e.from}
                               className={e.from.trim() && eventBadDates({...e, to: ''}) ? 'bad' : ''}
                               onChange={ev => setOwnEv(i, {from: ev.target.value})} />
                        <input placeholder="по (если период)" value={e.to}
                               className={e.to.trim() && eventBadDates({...e, from: ''}) ? 'bad' : ''}
                               onChange={ev => setOwnEv(i, {to: ev.target.value})} />
                        <span className="evmove">
                          <button type="button" className="icobtn" title="Выше" disabled={i === 0}
                                  onClick={() => moveOwn(i, -1)}>
                            <IconArrowUp size={15} stroke={1.8} />
                          </button>
                          <button type="button" className="icobtn" title="Ниже"
                                  disabled={i === own.length - 1}
                                  onClick={() => moveOwn(i, 1)}>
                            <IconArrowDown size={15} stroke={1.8} />
                          </button>
                          <button type="button" className="icobtn" title="Убрать событие"
                                  onClick={() => dropOwn(i)}>
                            <IconTrash size={15} stroke={1.7} />
                          </button>
                        </span>
                      </div>
                      <input placeholder="пояснение, если нужно" value={e.desc}
                             onChange={ev => setOwnEv(i, {desc: ev.target.value})} />
                    </div>
                  ))}
                  <button type="button" className="btn addrow"
                          onClick={() => setOwn(list => [...list, emptyEvent()])}>
                    <IconPlus size={14} stroke={2} /> Добавить событие
                  </button>
                  <span className="hint">
                    Здесь всё: рождение, смерть, проживание, служба. Дату можно
                    неполно — <b>1932</b> только год, <b>03.1932</b> месяц и год;
                    второе поле нужно, если событие длилось (жил с 1979 по 2005).
                    Событие без даты тоже имеет смысл: «Смерть» без даты — это
                    «человека нет, а когда — неизвестно».
                  </span>
                </div>
              )}

              <div className="evlist">
                {shownEvents.map((e, i) => {
                  const Ico = EVENT_ICON[e.type] || IconCalendarEvent
                  const sub = [e.desc, e.place].filter(Boolean).join(' · ')
                  return (
                    <div className={'evrow ' + e.cat} key={i}>
                      <span className="evicon"><Ico size={16} stroke={1.6} /></span>
                      <span className="evmain">
                        <span className="evtype">{EVENT_RU[e.type] || typeRu(e.type)}</span>
                        {sub && <span className="evsub">{sub}</span>}
                      </span>
                      <span className="evwhen">{e.date || '—'}</span>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          <div className="block">
            <div className="blabel">Работа</div>
            {!editing && (
              jobs.filter(j => !jobIsEmpty(j)).length ? (
                <ul className="plain">
                  {jobs.filter(j => !jobIsEmpty(j)).map((j, i) => <li key={i}>{jobLabel(j)}</li>)}
                </ul>
              ) : <div className="muted">не заполнено</div>
            )}
            {editing && jobs.map((j, i) => (
              <div className="job" key={j.handle || `new${i}`}>
                <div className="two">
                  <input placeholder="Профессия" value={j.profession}
                         onChange={e => setJob(i, {profession: e.target.value})} />
                  <input placeholder="Предприятие" value={j.employer}
                         onChange={e => setJob(i, {employer: e.target.value})} />
                </div>
                <div className="jobdates">
                  <input placeholder="с (год)" value={j.from}
                         className={j.from.trim() && jobBadDates({...j, to: ''}) ? 'bad' : ''}
                         onChange={e => setJob(i, {from: e.target.value})} />
                  <input placeholder="по (год)" value={j.to}
                         className={j.to.trim() && jobBadDates({...j, from: ''}) ? 'bad' : ''}
                         onChange={e => setJob(i, {to: e.target.value})} />
                  <button type="button" className="icobtn" title="Убрать запись"
                          onClick={() => setJobs(list => list.filter((_, k) => k !== i))}>
                    <IconTrash size={15} stroke={1.7} />
                  </button>
                </div>
              </div>
            ))}
            {editing && (
              <button type="button" className="btn addrow"
                      onClick={() => setJobs(list => [...list, emptyJob()])}>
                <IconPlus size={14} stroke={2} /> Добавить место работы
              </button>
            )}
          </div>

          <div className="block">
            <div className="blabel">Заметки</div>
            {!editing && (
              notes.some(n => n.text.trim()) ? (
                <div className="notes">
                  {notes.filter(n => n.text.trim()).map((n, i) => (
                    <div className="note" key={n.handle || i}>
                      {n.label && <span className="ntag">{n.label}</span>}
                      {n.text}
                    </div>
                  ))}
                </div>
              ) : <div className="muted">не заполнено</div>
            )}
            {editing && notes.map((n, i) => (
              <div className="noteedit" key={n.handle || `new${i}`}>
                <textarea value={n.text} rows={4}
                          onChange={e => setNotes(list =>
                            list.map((x, k) => (k === i ? {...x, text: e.target.value} : x)))} />
                <button type="button" className="icobtn" title="Убрать заметку"
                        onClick={() => setNotes(list => list.filter((_, k) => k !== i))}>
                  <IconTrash size={15} stroke={1.7} />
                </button>
              </div>
            ))}
            {editing && (
              <button type="button" className="btn addrow"
                      onClick={() => setNotes(list => [...list, {handle: null, text: '', label: ''}])}>
                <IconPlus size={14} stroke={2} /> Добавить заметку
              </button>
            )}
          </div>

          {(gallery.length > 0 || editing) && (
            <div className="block">
              <div className="blabel">Галерея {gallery.length + addMedia.length || ''}</div>
              {gallery.length + addMedia.length ? (
                <div className="pgal">
                  {gallery.map(m => (
                    <span className={'shot' + (m === mainMedia ? ' main' : '')} key={m}>
                      {/* Открываем тот же просмотрщик, что и в галерее: с
                          отметкой людей, датой, описанием и увеличением.
                          Раньше ссылка вела на голый файл в новой вкладке —
                          оттуда нельзя было ни отметить, ни подписать. */}
                      <button type="button" className="shotopen"
                              onClick={() => setViewing(gallery.indexOf(m))}>
                        {looksVideo(ix.media.get(m)?.mime) ? (
                          /* У ролика миниатюры нет — сервер строит их
                             картинкой. Показываем первый кадр файла. */
                          <span className="vthumb">
                            <video src={`/api/media/${m}/file?jwt=${jwt}#t=0.1`}
                                   preload="metadata" muted playsInline />
                            <span className="playmark">
                              <IconPlayerPlay size={18} stroke={2} />
                            </span>
                          </span>
                        ) : (
                          <img src={`/api/media/${m}/thumbnail/240?jwt=${jwt}`}
                               alt="" loading="lazy" />
                        )}
                      </button>
                      {m === mainMedia && <span className="mainmark">главное</span>}
                      {editing && !looksVideo(ix.media.get(m)?.mime) && (
                        <>
                          {/* Главное фото — просто первое в списке; при
                              удалении снимка на его место вставало
                              случайное, и поправить это было нечем. */}
                          {m !== mainMedia && (
                            <button type="button" className="drop star"
                                    title="Сделать главным снимком"
                                    onClick={() => { setMainMedia(m); setMainRect(null) }}>
                              <IconStar size={13} stroke={2} />
                            </button>
                          )}
                          {m === mainMedia && (
                            <button type="button" className="drop crop"
                                    title="Выбрать лицо для аватарки"
                                    onClick={() => setCropping(m)}>
                              <IconCrop size={13} stroke={2} />
                            </button>
                          )}
                        </>
                      )}
                      {editing && (
                        <button type="button" className="drop" title="Убрать из галереи этого человека"
                                onClick={() => setDropMedia(list => [...list, m])}>
                          <IconX size={13} stroke={2.2} />
                        </button>
                      )}
                    </span>
                  ))}
                  {addMedia.map((b, i) => <NewShot key={i} blob={b} />)}
                </div>
              ) : <div className="muted">пока пусто</div>}
              {editing && (
                <>
                  <input ref={galRef} type="file" accept="image/*,video/*" multiple hidden
                         onChange={e => {
                           const picked = [...(e.target.files || [])]
                           const bad = picked.find(b => !isMedia(b) || b.size > sizeLimit(b))
                           if (bad) { setErr('Годятся картинки до 20 МБ и видео до 300 МБ'); return }
                           setErr('')
                           setAddMedia(list => [...list, ...picked])
                           e.target.value = ''
                         }} />
                  <button type="button" className="btn addrow" onClick={() => galRef.current?.click()}>
                    <IconPlus size={14} stroke={2} /> Добавить снимки или видео
                  </button>
                  {(addMedia.length > 0 || dropMedia.length > 0) && (
                    <span className="hint">
                      {addMedia.length > 0 && `${addMedia.length} новых — загрузятся при сохранении. `}
                      {dropMedia.length > 0 &&
                        `Убрано: ${dropMedia.length}. Снимки останутся в общем архиве — ` +
                        `отвязываем только от этого человека.`}
                    </span>
                  )}
                </>
              )}
            </div>
          )}

          {(cites.length > 0 || editing) && (
            <div className="block">
              <div className="blabel">Цитаты</div>
              {cites.length ? (
                <div className="evlist">
                  {cites.map((c, i) => (
                    <div className="evrow" key={i}>
                      <span className="evtype">{c.source || 'без источника'}</span>
                      <span className="evwhen">{CONFIDENCE[c.conf] || ''}</span>
                      <span className="evwhere">{c.page}</span>
                    </div>
                  ))}
                </div>
              ) : <div className="muted">ссылок на источники нет</div>}
              {editing && (
                <span className="hint">
                  Источники пока заводятся в Gramps Web — здесь они только показываются.
                </span>
              )}
            </div>
          )}

          {meta && (
            <div className="block">
              <div className="blabel">Метаданные</div>
              <dl className="pview">
                <div className="fact">
                  <dt>Идентификатор</dt><dd className="mono">{meta.id || '—'}</dd>
                </div>
                <div className="fact">
                  <dt>Изменено</dt><dd>{stamp(meta.changed)}</dd>
                </div>
                <div className="fact">
                  <dt>Видимость</dt><dd>{meta.priv ? 'приватная запись' : 'открыта всем'}</dd>
                </div>
              </dl>
            </div>
          )}
        </div>
      ) : (
        !err && <div className="muted">Загружаем…</div>
      )}

      {editing && (
        <div className="pbar bottom">
          <button type="button" className="btn warn" onClick={() => setKilling(true)}>
            <IconTrash size={15} stroke={1.7} /> Удалить человека
          </button>
          <div className="spacer" />
          <button type="button" className="btn"
                  onClick={() => { setEditing(false); setReloadForm(n => n + 1); setErr('') }}>
            <IconX size={15} stroke={1.8} /> Отмена
          </button>
          <button className="btn primary" disabled={!canSave}>
            <IconDeviceFloppy size={15} stroke={1.7} />
            {busy ? 'Сохраняем…' : 'Сохранить'}
          </button>
        </div>
      )}

      {/* Кого добавляем: уже заведённого или нового. Раньше было только
          первое, и только в режиме правки — оттого и «непонятно». */}
      {addKid?.mode === 'pick' && (
        <Overlay><div className="modal-bg" onClick={() => setAddKid(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="mhead">
              <h2>Добавить ребёнка</h2>
              <button type="button" className="close" onClick={() => setAddKid(null)}
                      aria-label="Закрыть">
                <IconX size={18} stroke={1.8} />
              </button>
            </div>

            <label>
              Он уже есть в древе
              <PersonPicker ix={ix} value={null} exclude={busyAll}
                            placeholder="начните вводить имя"
                            onChange={h => h && addExistingKid(addKid.family, h)} />
            </label>

            <div className="orline"><span>или</span></div>

            <button type="button" className="btn primary wide"
                    onClick={() => setAddKid({...addKid, mode: 'new'})}>
              <IconPlus size={15} stroke={2} /> Завести нового человека
            </button>

            {err && <div className="err inline">{err}</div>}
          </div>
        </div></Overlay>
      )}

      {newKid !== null && kin && (
        <NewPersonDialog
          ix={ix}
          onClose={() => setNewKid(null)}
          onCreated={h => {
            setUnion(newKid, {children: [...(kin.unions[newKid]?.children || []), h]})
            setNewKid(null)
          }}
        />
      )}

      {addKid?.mode === 'new' && (
        <NewPersonDialog
          ix={ix}
          relation={{role: 'son', to: handle, family: addKid.family || undefined}}
          onClose={() => setAddKid(null)}
          onCreated={() => { setAddKid(null); onSaved(); setReloadForm(n => n + 1) }}
        />
      )}

      {viewing !== null && gallery[viewing] && (
        <MediaViewer
          ix={ix}
          handles={gallery}
          index={viewing}
          onIndex={setViewing}
          onClose={() => setViewing(null)}
          onOpenPerson={onOpenPerson}
          onChanged={() => { onSaved(); setReloadForm(n => n + 1) }}
        />
      )}

      {picking && (
        <MediaPicker ix={ix} person={handle} have={gallery}
                     onClose={() => setPicking(false)}
                     onPick={h => {
                       setMainMedia(h)
                       setMainRect(null)
                       setPhotoOut(false)
                       setPhotoBlob(null)
                       if (!gallery.includes(h)) setAddExisting(list => [...list, h])
                       setPicking(false)
                     }} />
      )}

      {cropping && (
        <CropPicker media={cropping} rect={mainRect}
                    onClose={() => setCropping(null)}
                    onDone={r => { setMainRect(r); setCropping(null) }} />
      )}

      {killing && (
        <Overlay><div className="modal-bg" onClick={() => setKilling(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="mhead">
              <h2>Удалить человека?</h2>
              <button type="button" className="close" onClick={() => setKilling(false)}
                      aria-label="Закрыть">
                <IconX size={18} stroke={1.8} />
              </button>
            </div>

            <div className="killwho">
              <Portrait p={person} size={64} shape="rounded" />
              <div>
                <b>{fullName(person)}</b>
                <div className="muted">{lifespan(person) || 'даты неизвестны'}</div>
              </div>
            </div>

            <div className="mnote">
              Запись исчезнет насовсем, вместе со связями: {[
                kin?.father || kin?.mother ? 'родители' : '',
                (kin?.unions || []).some(u => u.spouse) ? 'супруг' : '',
                allKids.length ? `дети (${allKids.length})` : '',
                gallery.length ? `фотографии (${gallery.length})` : '',
              ].filter(Boolean).join(', ') || 'связей нет'}.
              Сами люди и снимки останутся, пропадёт только эта карточка.
            </div>

            {err && <div className="err inline">{err}</div>}

            <div className="actions">
              <button type="button" className="btn" onClick={() => setKilling(false)}>Отмена</button>
              <button type="button" className="btn warn" disabled={busy} onClick={kill}>
                {busy ? 'Удаляем…' : 'Удалить'}
              </button>
            </div>
          </div>
        </div></Overlay>
      )}
    </form>
  )
}

/** Люди карточками со ссылками — родители, дети, братья и сёстры. */
function KinCards({list, ix, onGo, empty}: {
  list: string[]
  ix: TreeIndex
  onGo: (h: string) => void
  empty: string
}) {
  if (!list.length) return <div className="muted">{empty}</div>
  return (
    <div className="kinlist">
      {list.map(h => (
        <button type="button" key={h} className="mini" onClick={() => onGo(h)}>
          <Portrait p={ix.people.get(h)} size={56} />
          <span className="mn">{shortName(ix.people.get(h))}</span>
          <span className="md">{lifespan(ix.people.get(h))}</span>
        </button>
      ))}
    </div>
  )
}

/** Координаты места, если они заполнены — для ссылки на карту. */
function coordsOf(ix: TreeIndex, place: string | null): {lat: string; long: string} | null {
  const n = place ? ix.places.get(place) : null
  if (!n?.lat || !n?.long) return null
  return {lat: n.lat, long: n.long}
}
